// autopilot-control-directive-shared.ts
//
// Hạ tầng chung cho các lệnh control (approve/pause/resume/answer/redirect/
// revert/abort): kiểu deps + hàm ghi directive vào inbox.
//
// Tách khỏi `autopilot-control-directive-commands.ts` (M1, code-reviewer
// 260916 phase-04): file đó bị P4 đẩy 196 → 252 dòng khi lệnh `answer` nhận
// thêm `--question` (B4) và `approve` nhận `--targets`/`--expires` (E1/H1).
// Tách phần dùng chung ra đây để cả hai file lệnh cùng dưới trần 200 dòng.
//
// KHÔNG đổi hành vi: mọi lệnh vẫn ghi CHỈ vào inbox qua directive-inbox.ts,
// không bao giờ chạm mission.json (spec §3.3 Req 4-7, tests 25-29).
import { writeMachineReadableJson } from "../machine-readable-json-output-contract.js";
import { log } from "../terminal-logger.js";
import {
  DELEGATED_REASON_MAX_CHARS,
  type DelegationFlags,
  delegationRejection,
  rejectControlCommand,
} from "./claude-code-shell-delegation-guard.js";
import { redactDecisionText } from "./decision-policy-journal-events.js";
import { delegatedResumeRejection } from "./delegated-resume-pause-owner-guard.js";
import { writeDirective } from "./directive-inbox.js";
import { resolveMission } from "./mission-resolver.js";
import { knownValuesFor } from "./mission-secrets-test-env-loader.js";

export interface ControlDirectiveDeps {
  resolveWorkspaceRoot: (repoFlag: string | undefined) => Promise<string>;
  machineLockPath: string;
  /** 06k C7: config global hiệu lực (đọc read-only cho Bảng G3 ủy nhiệm). */
  globalConfigPath?: string;
  /** 06k C6b: env của shell gọi lệnh — tiêm được cho test; mặc định process.env. */
  env?: NodeJS.ProcessEnv;
}

/** 06k C6/C7: cờ ủy nhiệm + kiểm thêm trước khi ghi (trả lý do ⇒ từ chối, không ghi). */
export interface ControlDirectiveOptions extends DelegationFlags {
  reasonRequired?: boolean;
  guard?: (mission: { missionId: string; controlRoot: string }) => Promise<string | undefined>;
}

export type ControlDirectiveKind =
  | "approve"
  | "pause"
  | "resume"
  | "answer"
  | "redirect"
  | "revert"
  | "abort"
  | "abort_confirm";

/** C6b áp cho các lệnh này; abort/abort_confirm/revert KHÔNG (review M1 — chat không tự huỷ được vẫn đúng I-3). */
const DELEGATION_KINDS: ReadonlySet<ControlDirectiveKind> = new Set([
  "approve",
  "answer",
  "redirect",
  "pause",
  "resume",
]);

/** [N16] Single source for CLI-local actor identity — shared with `issueAbortChallenge`/`confirmAbortChallenge` so N9's actor comparison never sees a mismatch. */
export function resolveCliActorId(): string {
  return process.env.USER ?? process.env.LOGNAME ?? "unknown";
}

export async function writeControlDirective(
  deps: ControlDirectiveDeps,
  workspaceRoot: string,
  missionFlag: string | undefined,
  kind: ControlDirectiveKind,
  payload: Record<string, unknown>,
  jsonOut: boolean,
  opts: ControlDirectiveOptions = {},
): Promise<void> {
  const command = `autopilot ${kind}`;
  const refused = DELEGATION_KINDS.has(kind)
    ? delegationRejection(deps.env ?? process.env, opts, opts.reasonRequired === true)
    : undefined;
  if (refused !== undefined) return rejectControlCommand(command, refused, jsonOut);
  const resolved = await resolveMission({ workspaceRoot, explicitMissionId: missionFlag });
  const delegated = opts.delegated === true;
  const guarded =
    (await opts.guard?.(resolved)) ??
    (kind === "resume" && delegated
      ? await delegatedResumeRejection(resolved.controlRoot)
      : undefined);
  if (guarded !== undefined) return rejectControlCommand(command, guarded, jsonOut);
  // C6: căn cứ ủy nhiệm — che known-values TRƯỚC, cắt 300 SAU, ngay lúc ghi directive.
  const rationale =
    delegated && opts.reason !== undefined
      ? redactDecisionText(opts.reason, await knownValuesFor(resolved.missionId)).slice(
          0,
          DELEGATED_REASON_MAX_CHARS,
        )
      : undefined;
  const receipt = await writeDirective({
    controlRoot: resolved.controlRoot,
    machineLockPath: deps.machineLockPath,
    directive: {
      mission_id: resolved.missionId,
      kind,
      source: delegated ? "chat_delegated" : "cli_local",
      actor_id: resolveCliActorId(),
      payload: rationale === undefined ? payload : { ...payload, rationale },
    },
  });
  if (jsonOut) {
    writeMachineReadableJson({ command, receipt });
  } else {
    log.info(
      `Directive ${receipt.directive_id}: ${receipt.status}${receipt.reason ? ` — ${receipt.reason}` : ""}`,
    );
  }
}
